import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { dropdownAnimation } from '@app/shared/animations/dropdown.animations';
import { IonPopover } from "@ionic/angular";
import { Capacitor } from '@capacitor/core';
import { DateTime } from 'luxon';
import { ConnectedPosition } from '@angular/cdk/overlay';

interface DesktopDatePickerDay {
  date: DateTime;
  label: string;
  outsideMonth: boolean;
  selected: boolean;
  today: boolean;
  disabled: boolean;
}

interface DesktopMonthPickerOption {
  value: number;
  label: string;
  selected: boolean;
  disabled: boolean;
}

interface DesktopTimeOption {
  value: number;
  label: string;
}

@Component({
  selector: 'app-date',
  templateUrl: './date.component.html',
  styleUrls: ['./date.component.scss'],
  animations: [dropdownAnimation()]
})
export class DateComponent implements OnInit {
  @Input() label: string | any = '';
  @Input() constraints = '';
  @Input() disabled = false;
  @Input() format: string | any = 'dd/MM/YY HH:mm';
  @Input() presentationType: string | 'date' | 'time' | 'date-time' = 'date-time';
  @Input() id: string | undefined;
  @Input() pickerSize: 'auto' | 'cover' = 'auto'; // 'auto' for desktop, 'cover' for mobile
  @Input() pickerstyle: 'popover' | 'modal' = 'popover';
  @Input() required = false;
  @Input() requiredLabel = false;
  @Input() nowButtonEnable = false;
  @Input() inModal = false;
  /**
   * Emit a full ISO datetime instead of a bare date / bare HH:mm. Set this when the
   * bound control holds a datetime but is edited by a date-only or time-only widget,
   * so the half this widget does not own is preserved rather than dropped.
   */
  @Input() emitFullDateTime = false;

  lvMaxNumber: number | null = null;
  lvMinNumber: number | null = null;
  lvMaxDate: string | null = null;

  @Output() nowButtonDataChange = new EventEmitter<any>();
  @Output() dateChange = new EventEmitter<any>();

  @ViewChild(IonPopover) popover!: IonPopover;

  expanded = false;
  useDesktopPicker = false;
  isNativePicker = false;
  localValue: any = '';

  desktopDraftValue = DateTime.now();
  desktopDisplayMonth = DateTime.now().startOf('month');
  desktopHourValue: number = DateTime.now().hour;
  desktopMinuteValue: number = DateTime.now().minute;
  desktopCalendarView: 'days' | 'months' = 'days';
  desktopTimeExpanded = false;
  desktopYearValue: number = DateTime.now().year;
  desktopCalendarDays: DesktopDatePickerDay[] = [];
  desktopMonthOptions: DesktopMonthPickerOption[] = [];
  readonly desktopWeekdays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  readonly desktopMonthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  readonly desktopHourOptions: DesktopTimeOption[] = Array.from({ length: 24 }, (_, hour) => ({
    value: hour,
    label: this.formatDesktopTimePart(hour)
  }));
  readonly desktopMinuteOptions: DesktopTimeOption[] = Array.from({ length: 60 }, (_, minute) => ({
    value: minute,
    label: this.formatDesktopTimePart(minute)
  }));
  readonly desktopOverlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -4
    }
  ];

  constructor() {
  }

  get minNumber(): number | null {
    return this.lvMinNumber;
  }

  @Input() set minNumber(value: number | null) {
    this.lvMinNumber = value;
  }

  get maxNumber(): number | null {
    return this.lvMaxNumber;
  }

  @Input() set maxNumber(value: number | null) {
    this.lvMaxNumber = value;
  }

  get maxDate(): string | null {
    return this.lvMaxDate;
  }

  @Input() set maxDate(value: string | null) {
    this.lvMaxDate = value;
    this.refreshDesktopCalendarDays();
  }

  get value(): string | any {
    return this.localValue;
  }

  @Input() set value(value: string | any) {
    this.localValue = value;
    if (this.useDesktopPicker) {
      this.syncDesktopDraftValue();
    }
  }

  ngOnInit(): void {
    this.isNativePicker = this.shouldUseNativePicker();
    this.useDesktopPicker = !this.isNativePicker;
    this.syncDesktopDraftValue();
  }

  // --- Ionic picker path (tablet / mobile-web) -------------------------------

  onDateChange(event: any) {
    this.localValue = event.detail.value;
    this.expanded = false;
    if (this.presentationType !== 'date-time') {
      if (!this.inModal && this.popover) {
        this.popover.dismiss();
      }
    }

    this.dateChange.emit(this.localValue);
  }

  onNowDateChange(event: any) {
    this.localValue = DateTime.now().toISO({ includeOffset: false }) ?? '';
    this.nowButtonDataChange.emit(this.localValue);
    if (!this.inModal && this.popover) {
      this.popover.dismiss();
    }
    this.expanded = false;
  }

  onCancel() {
    this.expanded = false;
  }

  showPicker() {
    if (this.useDesktopPicker) {
      return;
    }

    if (this.inModal) {
      this.expanded = !this.expanded;
    }
  }

  // --- Desktop picker --------------------------------------------------------

  get hasDateSelection(): boolean {
    return this.presentationType !== 'time';
  }

  get hasTimeSelection(): boolean {
    return this.presentationType === 'time' || this.presentationType === 'date-time';
  }

  get desktopMonthLabel(): string {
    return this.desktopDisplayMonth.toFormat('LLLL yyyy');
  }

  get desktopYearLabel(): string {
    return this.desktopYearValue.toString();
  }

  get desktopHourDisplayValue(): string {
    return this.formatDesktopTimePart(this.desktopHourValue);
  }

  get desktopMinuteDisplayValue(): string {
    return this.formatDesktopTimePart(this.desktopMinuteValue);
  }

  get displayValue(): string {
    if (!this.localValue) {
      return '';
    }

    const parsedValue = this.parseDateValue(this.localValue);
    if (!parsedValue.isValid) {
      return this.localValue;
    }

    if (this.presentationType === 'time') {
      return parsedValue.toFormat('HH:mm');
    }
    if (this.presentationType === 'date') {
      return parsedValue.toFormat('dd/MM/yy');
    }
    return parsedValue.toFormat('dd/MM/yy HH:mm');
  }

  get maxDateTime(): DateTime | null {
    if (!this.maxDate) {
      return null;
    }

    const parsedMaxDate = DateTime.fromISO(this.maxDate);
    return parsedMaxDate.isValid ? parsedMaxDate : null;
  }

  toggleDesktopPicker(event?: Event): void {
    if (!this.useDesktopPicker || this.disabled) {
      return;
    }

    event?.preventDefault();
    event?.stopPropagation();
    this.expanded ? this.closeDesktopPicker() : this.openDesktopPicker();
  }

  openDesktopPicker(): void {
    this.syncDesktopDraftValue();
    this.expanded = true;
  }

  closeDesktopPicker(): void {
    this.expanded = false;
    this.desktopCalendarView = 'days';
    this.desktopTimeExpanded = false;
  }

  previousDesktopPeriod(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.desktopCalendarView === 'months') {
      this.desktopYearValue -= 1;
      this.refreshDesktopMonthOptions();
      return;
    }

    this.desktopDisplayMonth = this.desktopDisplayMonth.minus({ months: 1 });
    this.refreshDesktopCalendarDays();
  }

  nextDesktopPeriod(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.desktopCalendarView === 'months') {
      this.desktopYearValue += 1;
      this.refreshDesktopMonthOptions();
      return;
    }

    this.desktopDisplayMonth = this.desktopDisplayMonth.plus({ months: 1 });
    this.refreshDesktopCalendarDays();
  }

  toggleDesktopMonthPicker(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.desktopCalendarView = this.desktopCalendarView === 'months' ? 'days' : 'months';
    this.desktopYearValue = this.desktopDisplayMonth.year;
    this.refreshDesktopMonthOptions();
  }

  selectDesktopMonth(month: DesktopMonthPickerOption, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (month.disabled) {
      return;
    }

    this.desktopDisplayMonth = this.desktopDisplayMonth.set({
      year: this.desktopYearValue,
      month: month.value
    }).startOf('month');
    this.desktopCalendarView = 'days';
    this.refreshDesktopCalendarDays();
  }

  selectDesktopDay(day: DesktopDatePickerDay, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (day.disabled) {
      return;
    }

    this.desktopDraftValue = this.mergeDateAndTime(day.date);
    this.desktopDisplayMonth = day.date.startOf('month');
    this.refreshDesktopCalendarDays();

    if (this.presentationType === 'date') {
      this.commitDesktopSelection();
    }
  }

  toggleDesktopTimePicker(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.desktopTimeExpanded = !this.desktopTimeExpanded;
  }

  selectDesktopHour(hour: DesktopTimeOption, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.desktopHourValue = hour.value;
    this.desktopDraftValue = this.mergeDateAndTime(this.desktopDraftValue);
  }

  selectDesktopMinute(minute: DesktopTimeOption, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.desktopMinuteValue = minute.value;
    this.desktopDraftValue = this.mergeDateAndTime(this.desktopDraftValue);
  }

  formatDesktopTimePart(value: number): string {
    return value.toString().padStart(2, '0');
  }

  useCurrentDesktopValue(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const now = DateTime.now();
    this.desktopDraftValue = now;
    this.desktopDisplayMonth = now.startOf('month');
    this.desktopHourValue = now.hour;
    this.desktopMinuteValue = now.minute;
    this.refreshDesktopCalendarDays();
    if (this.presentationType !== 'date-time') {
      this.commitDesktopSelection();
    }
  }

  applyDesktopSelection(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.commitDesktopSelection();
  }

  cancelDesktopSelection(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.syncDesktopDraftValue();
    this.closeDesktopPicker();
  }

  // --- Native input fallback (mobile / Capacitor) ----------------------------

  get nativeInputType(): string {
    if (this.presentationType === 'time') {
      return 'time';
    }
    if (this.presentationType === 'date') {
      return 'date';
    }
    return 'datetime-local';
  }

  get nativeInputValue(): string {
    if (!this.localValue) {
      return '';
    }
    const parsed = this.parseDateValue(this.localValue);
    if (!parsed.isValid) {
      return '';
    }
    if (this.presentationType === 'time') {
      return parsed.toFormat('HH:mm');
    }
    if (this.presentationType === 'date') {
      return parsed.toISODate() ?? '';
    }
    return parsed.toFormat("yyyy-MM-dd'T'HH:mm");
  }

  get nativeMaxValue(): string {
    if (!this.maxDate) {
      return '';
    }
    const parsed = DateTime.fromISO(this.maxDate);
    if (!parsed.isValid) {
      return '';
    }
    if (this.presentationType === 'time') {
      return parsed.toFormat('HH:mm');
    }
    if (this.presentationType === 'date') {
      return parsed.toISODate() ?? '';
    }
    return parsed.toFormat("yyyy-MM-dd'T'HH:mm");
  }

  openNativePicker(input: HTMLInputElement): void {
    if (this.disabled) {
      return;
    }

    // Chrome only opens the picker from its calendar indicator, which this input hides,
    // so a click on the transparent overlay does nothing. showPicker() opens it directly.
    // It must run inside the user gesture, and on browsers without it a tap on the input
    // already opens the OS picker natively.
    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker();
      } catch {
        // No user activation, or the browser declined — the native behaviour still applies.
      }
    }
  }

  onNativeInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.value) {
      return;
    }
    if (this.presentationType === 'time') {
      this.localValue = input.value;
    } else {
      this.localValue = DateTime.fromISO(input.value).toISO({ includeOffset: false }) ?? '';
    }
    this.dateChange.emit(this.localValue);
  }

  // --- Internals -------------------------------------------------------------

  private shouldUseNativePicker(): boolean {
    // Only real native builds get the OS picker. Every browser uses the in-app calendar:
    // the browser's own picker renders outside the page and cannot be themed to match.
    return Capacitor.isNativePlatform();
  }

  private refreshDesktopCalendarDays(): void {
    const firstDay = this.desktopDisplayMonth.startOf('month');
    // Luxon weeks are ISO, so startOf('week') is the Monday of that week.
    const firstVisibleDay = firstDay.startOf('week');
    const maxDate = this.maxDateTime?.startOf('day') ?? null;

    this.desktopCalendarDays = Array.from({ length: 42 }).map((_, index) => {
      const date = firstVisibleDay.plus({ days: index });
      return {
        date,
        label: date.day.toString(),
        outsideMonth: !date.hasSame(this.desktopDisplayMonth, 'month'),
        selected: this.hasDateSelection && date.hasSame(this.desktopDraftValue, 'day'),
        today: date.hasSame(DateTime.now(), 'day'),
        disabled: !!maxDate && date.startOf('day') > maxDate
      };
    });
    this.refreshDesktopMonthOptions();
  }

  private refreshDesktopMonthOptions(): void {
    const maxDate = this.maxDateTime?.startOf('month') ?? null;
    const selectedMonth = this.desktopDisplayMonth.startOf('month');

    this.desktopMonthOptions = this.desktopMonthLabels.map((label, index) => {
      const monthDate = DateTime.local(this.desktopYearValue, index + 1, 1);
      return {
        value: index + 1,
        label,
        selected: monthDate.hasSame(selectedMonth, 'month'),
        disabled: !!maxDate && monthDate > maxDate
      };
    });
  }

  private syncDesktopDraftValue(): void {
    const parsedValue = this.parseDateValue(this.localValue);
    this.desktopDraftValue = parsedValue.isValid ? parsedValue : DateTime.now();
    this.desktopDisplayMonth = this.desktopDraftValue.startOf('month');
    this.desktopCalendarView = 'days';
    this.desktopTimeExpanded = false;
    this.desktopYearValue = this.desktopDisplayMonth.year;
    this.desktopHourValue = this.desktopDraftValue.hour;
    this.desktopMinuteValue = this.desktopDraftValue.minute;
    this.refreshDesktopCalendarDays();
  }

  private parseDateValue(value: string | any): DateTime {
    if (!value) {
      return DateTime.invalid('No date value');
    }

    const isoValue = DateTime.fromISO(value);
    if (isoValue.isValid) {
      return isoValue;
    }

    const timeValue = DateTime.fromFormat(value, 'HH:mm');
    if (timeValue.isValid) {
      const now = DateTime.now();
      return now.set({
        hour: timeValue.hour,
        minute: timeValue.minute,
        second: 0,
        millisecond: 0
      });
    }

    return DateTime.invalid('Unsupported date value');
  }

  private mergeDateAndTime(date: DateTime): DateTime {
    return date.set({
      hour: this.desktopHourValue,
      minute: this.desktopMinuteValue,
      second: 0,
      millisecond: 0
    });
  }

  private commitDesktopSelection(): void {
    this.localValue = this.formatDesktopOutputValue();
    this.dateChange.emit(this.localValue);
    this.closeDesktopPicker();
  }

  private formatDesktopOutputValue(): string {
    // desktopDraftValue already carries both halves: syncDesktopDraftValue() seeds the
    // time from the current value (or now, when empty) and selectDesktopDay() merges it
    // back in, so emitting the full ISO preserves whichever half this widget does not own.
    if (!this.emitFullDateTime) {
      if (this.presentationType === 'time') {
        return this.desktopDraftValue.toFormat('HH:mm');
      }
      if (this.presentationType === 'date') {
        return this.desktopDraftValue.toISODate() ?? '';
      }
    }

    return this.desktopDraftValue.toISO({ includeOffset: false, suppressMilliseconds: true }) ?? '';
  }
}
